"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  ResultBanner,
  PrintButton,
  CopyButton,
  TableScroll,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";
import { riskScore, riskBand, RISK_BAND_LABEL } from "@/lib/templateContent";

/* Standard UK 5×5 risk matrix: Risk = Likelihood (1–5) × Severity (1–5), giving
   a score of 1–25. Bands reuse the site's existing convention (lib/templateContent
   riskBand) so the tool matches the RAMS builder exactly:
     ≤4   Low        — acceptable; maintain existing controls
     5–9  Medium     — tolerable with documented controls; reduce where practicable
     10–15 High      — act before work starts; further controls needed
     16–25 Very high — stop; do not proceed until risk is reduced
   Scoring is a prioritisation aid, not a compliance outcome — a competent person
   must review every rating against the specific task and site. */

const LIKELIHOOD: { value: number; label: string; hint: string }[] = [
  { value: 1, label: "1 — Rare", hint: "Extremely unlikely; would only happen in exceptional circumstances" },
  { value: 2, label: "2 — Unlikely", hint: "Not expected to happen, but possible" },
  { value: 3, label: "3 — Possible", hint: "Might happen occasionally" },
  { value: 4, label: "4 — Likely", hint: "Will probably happen at some point" },
  { value: 5, label: "5 — Almost certain", hint: "Expected to happen, often or repeatedly" },
];

const SEVERITY: { value: number; label: string; hint: string }[] = [
  { value: 1, label: "1 — Negligible", hint: "No or minor injury; first aid only" },
  { value: 2, label: "2 — Minor", hint: "Minor injury; short absence (a few days)" },
  { value: 3, label: "3 — Moderate", hint: "Injury requiring treatment; over a week off" },
  { value: 4, label: "4 — Major", hint: "Serious injury; long-term harm or disability" },
  { value: 5, label: "5 — Catastrophic", hint: "Fatality or multiple serious injuries" },
];

type BandKey = "low" | "medium" | "high" | "very-high";

/** Maps the site's band key (hyphenated) to the Verdict/CSS token (no hyphen). */
const BAND_TO_VERDICT: Record<BandKey, Verdict> = {
  low: "low",
  medium: "medium",
  high: "high",
  "very-high": "veryhigh",
};

interface BandMeaning {
  title: string;
  advice: string;
}

/* What each band means for a RAMS — framed as prioritisation, never as a sign-off.
   Never tells the user the work is "safe" or "compliant". */
function bandMeaning(band: BandKey, score: number): BandMeaning {
  const label = RISK_BAND_LABEL[band];
  switch (band) {
    case "low":
      return {
        title: `${score} — ${label} risk`,
        advice:
          "Generally acceptable. Keep the existing controls in place, record the rating in your risk assessment and review it if the task or site changes. A low score is a prioritisation result, not confirmation the work is safe — a competent person should still sense-check it.",
      };
    case "medium":
      return {
        title: `${score} — ${label} risk`,
        advice:
          "Tolerable only with documented controls. Reduce the risk as far as is reasonably practicable, write the controls into the method statement and brief the team before work starts. Re-score the residual risk once those controls are applied.",
      };
    case "high":
      return {
        title: `${score} — ${label} risk`,
        advice:
          "Act before work begins. Additional controls are needed to bring this down — do not start the task on this rating. Plan the extra measures, agree them with the competent person and re-score the residual risk; the work should normally only proceed once it sits in a lower band.",
      };
    case "very-high":
      return {
        title: `${score} — ${label} risk`,
        advice:
          "Stop. Work must not go ahead on this rating. Eliminate the hazard or redesign the task, then re-assess. If the residual risk cannot be brought down to a tolerable level by reasonably practicable controls, the activity should not be carried out as planned.",
      };
  }
}

interface Scoring {
  likelihood: number;
  severity: number;
}

/** The interactive 5×5 grid. Clicking a cell sets likelihood (column) × severity (row). */
function MatrixGrid({
  value,
  onPick,
  idPrefix,
}: {
  value: Scoring;
  onPick: (s: Scoring) => void;
  idPrefix: string;
}) {
  // Severity runs top (5, most severe) to bottom (1); likelihood left (1) to right (5).
  const severities = [5, 4, 3, 2, 1];
  const likelihoods = [1, 2, 3, 4, 5];
  return (
    <div className="overflow-x-auto">
      <table className="border-separate border-spacing-1 text-center text-sm" aria-label="5 by 5 risk matrix">
        <thead>
          <tr>
            <th className="w-8" aria-hidden />
            <th className="w-8" aria-hidden />
            <th colSpan={5} className="pb-1 text-[0.74rem] font-semibold uppercase tracking-wide text-muted">
              Likelihood →
            </th>
          </tr>
          <tr>
            <th aria-hidden />
            <th aria-hidden />
            {likelihoods.map((l) => (
              <th key={l} scope="col" className="w-12 pb-1 text-[0.8rem] font-semibold text-ink">
                {l}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {severities.map((sev, i) => (
            <tr key={sev}>
              {i === 0 && (
                <th
                  rowSpan={5}
                  scope="row"
                  className="w-6 align-middle text-[0.74rem] font-semibold uppercase tracking-wide text-muted"
                >
                  <span className="inline-block -rotate-180 [writing-mode:vertical-rl]">Severity →</span>
                </th>
              )}
              <th scope="row" className="w-8 pr-1 text-[0.8rem] font-semibold text-ink">
                {sev}
              </th>
              {likelihoods.map((lik) => {
                const score = lik * sev;
                const band = riskBand(score) as BandKey;
                const selected = value.likelihood === lik && value.severity === sev;
                return (
                  <td key={lik} className="p-0">
                    <button
                      type="button"
                      id={`${idPrefix}-cell-${lik}-${sev}`}
                      onClick={() => onPick({ likelihood: lik, severity: sev })}
                      aria-pressed={selected}
                      aria-label={`Likelihood ${lik}, severity ${sev}, score ${score}, ${RISK_BAND_LABEL[band]} risk`}
                      className={`risk-${band} flex h-11 w-12 items-center justify-center rounded-md font-display text-[0.95rem] font-bold transition-all ${
                        selected
                          ? "scale-105 ring-2 ring-ink ring-offset-1"
                          : "opacity-90 hover:scale-105 hover:opacity-100"
                      }`}
                    >
                      {score}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** One full scoring panel: matrix grid + the two descriptive selects. */
function ScoringPanel({
  heading,
  description,
  value,
  onChange,
  idPrefix,
}: {
  heading: string;
  description: string;
  value: Scoring;
  onChange: (s: Scoring) => void;
  idPrefix: string;
}) {
  return (
    <div className="card shadow-soft p-6">
      <h2 className="text-xl">{heading}</h2>
      <p className="mt-1 text-sm text-muted">{description}</p>

      <div className="mt-5">
        <MatrixGrid value={value} onPick={onChange} idPrefix={idPrefix} />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Field
          label="Likelihood"
          hint={LIKELIHOOD.find((l) => l.value === value.likelihood)?.hint}
        >
          <SelectInput
            value={value.likelihood}
            onChange={(e) => onChange({ ...value, likelihood: parseInt(e.target.value, 10) })}
          >
            {LIKELIHOOD.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field
          label="Severity"
          hint={SEVERITY.find((s) => s.value === value.severity)?.hint}
        >
          <SelectInput
            value={value.severity}
            onChange={(e) => onChange({ ...value, severity: parseInt(e.target.value, 10) })}
          >
            {SEVERITY.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </SelectInput>
        </Field>
      </div>
    </div>
  );
}

/* One hazard in the register. The residual starts equal to the initial score
   when the user opts in — never pre-filled lower — and only "counts" once at
   least one control measure is recorded. */
interface HazardEntry {
  id: number;
  name: string;
  initial: Scoring;
  controls: string;
  showResidual: boolean;
  residual: Scoring;
}

let nextHazardId = 1;
function emptyHazard(): HazardEntry {
  return {
    id: nextHazardId++,
    name: "",
    initial: { likelihood: 3, severity: 3 },
    controls: "",
    showResidual: false,
    residual: { likelihood: 3, severity: 3 },
  };
}

function rate(s: Scoring) {
  const score = riskScore(s);
  const band = riskBand(score) as BandKey;
  return { score, band, verdict: BAND_TO_VERDICT[band], meaning: bandMeaning(band, score) };
}

function residualCounts(h: HazardEntry): boolean {
  return h.showResidual && h.controls.trim().length > 0;
}

export function RiskMatrixCalculator() {
  const [hazards, setHazards] = useState<HazardEntry[]>([emptyHazard()]);
  const [activeId, setActiveId] = useState<number>(() => hazards[0].id);
  const [assessor, setAssessor] = useState("");
  const resultTracked = useRef(false);

  const active = hazards.find((h) => h.id === activeId) ?? hazards[0];

  const patch = (id: number, p: Partial<HazardEntry>) =>
    setHazards((hs) => hs.map((h) => (h.id === id ? { ...h, ...p } : h)));

  const addHazard = () => {
    const h = emptyHazard();
    setHazards((hs) => [...hs, h]);
    setActiveId(h.id);
  };

  const duplicateHazard = (id: number) => {
    const src = hazards.find((h) => h.id === id);
    if (!src) return;
    const copy: HazardEntry = { ...src, id: nextHazardId++, name: src.name ? `${src.name} (copy)` : "" };
    setHazards((hs) => [...hs, copy]);
    setActiveId(copy.id);
  };

  const removeHazard = (id: number) => {
    setHazards((hs) => {
      const next = hs.filter((h) => h.id !== id);
      if (next.length === 0) return hs; // always keep one row
      if (id === activeId) setActiveId(next[0].id);
      return next;
    });
  };

  const initialResult = useMemo(() => rate(active.initial), [active.initial]);
  const residualResult = useMemo(() => rate(active.residual), [active.residual]);
  const residualActive = residualCounts(active);
  const activeResult = residualActive ? residualResult : initialResult;

  // Fire the funnel event once per visit when an initial rating exists.
  useEffect(() => {
    if (!resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("risk-matrix-calculator", {
        score: initialResult.score,
        band: initialResult.band,
      });
    }
  }, [initialResult]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="space-y-6">
        {/* Hazard register — score several hazards into one printable record. */}
        {hazards.length > 1 && (
          <div className="card shadow-soft p-6">
            <h2 className="text-xl">Hazard register</h2>
            <p className="mt-1 text-sm text-muted">
              Every hazard below prints into one register. Select a row to edit its scoring.
            </p>
            <ul className="mt-4 space-y-2">
              {hazards.map((h, i) => {
                const ini = rate(h.initial);
                const res = residualCounts(h) ? rate(h.residual) : null;
                const isActive = h.id === active.id;
                return (
                  <li
                    key={h.id}
                    className={`flex flex-wrap items-center gap-2 rounded-lg border px-3.5 py-2.5 ${
                      isActive ? "border-emerald bg-emerald-tint/40" : "border-line bg-mist/50"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveId(h.id)}
                      className="min-w-0 flex-1 text-left text-[0.9rem] font-semibold text-ink hover:underline"
                    >
                      {h.name.trim() || `Hazard ${i + 1}`}
                    </button>
                    <span className={`risk-chip risk-${ini.band}`}>{ini.score}</span>
                    {res && (
                      <>
                        <span className="text-muted" aria-hidden>→</span>
                        <span className={`risk-chip risk-${res.band}`}>{res.score}</span>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => duplicateHazard(h.id)}
                      className="text-[0.78rem] font-semibold text-muted hover:text-ink"
                    >
                      Duplicate
                    </button>
                    <button
                      type="button"
                      onClick={() => removeHazard(h.id)}
                      className="text-[0.78rem] font-semibold text-muted hover:text-risk-veryhigh"
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="card shadow-soft p-6">
          <Field label="Hazard being scored" hint="Appears on the printed register.">
            <TextInput
              value={active.name}
              onChange={(e) => patch(active.id, { name: e.target.value })}
              placeholder="e.g. Working from a mobile tower near an open edge"
            />
          </Field>
        </div>

        <ScoringPanel
          heading="Initial risk"
          description="Score the risk before any controls are applied — how bad it could be and how likely it is as the task stands."
          value={active.initial}
          onChange={(s) => patch(active.id, { initial: s })}
          idPrefix={`initial-${active.id}`}
        />

        {active.showResidual ? (
          <div className="space-y-4">
            <div className="card shadow-soft p-6">
              <Field
                label="Control measures"
                hint="What you'll actually do to reduce the risk — the residual score should only drop because of these."
              >
                <TextArea
                  value={active.controls}
                  onChange={(e) => patch(active.id, { controls: e.target.value })}
                  placeholder="e.g. Tower erected by PASMA-trained operative; guardrails fitted; exclusion zone below"
                />
              </Field>
            </div>
            {/* The residual can only be scored once at least one control is
                recorded — a tool must not imply risk reduction without controls. */}
            {residualActive ? (
              <ScoringPanel
                heading="Residual risk (after controls)"
                description="Re-score the same hazard once your control measures are in place. It starts at your initial score — reduce it only as far as the controls genuinely justify."
                value={active.residual}
                onChange={(s) => patch(active.id, { residual: s })}
                idPrefix={`residual-${active.id}`}
              />
            ) : (
              <p className="rounded-xl border border-line bg-mist/60 px-5 py-4 text-sm text-muted">
                Enter the control measures above to unlock residual scoring — the rating should only come
                down because of what you'll actually do.
              </p>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() =>
              // Start the residual at the initial rating — the user lowers it
              // by entering controls, not by default.
              patch(active.id, { residual: { ...active.initial }, showResidual: true })
            }
            className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
          >
            + Add residual risk (after controls)
          </button>
        )}

        <button
          type="button"
          onClick={addHazard}
          className="inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
        >
          + Add another hazard to the register
        </button>
      </div>

      {/* Result + printable record */}
      <div className="space-y-4">
        <div className="print-area card shadow-soft p-6" id="risk-matrix-record">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
            Risk rating record
          </div>
          <h2 className="mt-1 text-lg">
            {hazards.length > 1
              ? `${hazards.length} hazards — `
              : active.name.trim()
                ? `${active.name.trim()} — `
                : ""}
            {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </h2>

          <div className="mt-4">
            <ResultBanner level={activeResult.verdict} title={activeResult.meaning.title}>
              {activeResult.meaning.advice}
            </ResultBanner>
          </div>

          {/* Before → after summary */}
          <div className="mt-4 grid grid-cols-[1fr_auto_1fr] items-stretch gap-2 text-center">
            <div className="rounded-lg bg-mist px-2 py-3">
              <div className="text-[0.72rem] font-semibold uppercase tracking-wide text-muted">Initial</div>
              <div className={`risk-chip risk-${initialResult.band} mx-auto mt-1.5`}>{initialResult.score}</div>
              <div className="mt-1.5 text-[0.74rem] text-muted">
                L{active.initial.likelihood} × S{active.initial.severity}
              </div>
              <div className="text-[0.78rem] font-semibold text-ink">{RISK_BAND_LABEL[initialResult.band]}</div>
            </div>
            <div className="flex items-center justify-center text-2xl text-muted" aria-hidden>
              →
            </div>
            <div className="rounded-lg bg-mist px-2 py-3">
              <div className="text-[0.72rem] font-semibold uppercase tracking-wide text-muted">Residual</div>
              {residualActive ? (
                <>
                  <div className={`risk-chip risk-${residualResult.band} mx-auto mt-1.5`}>{residualResult.score}</div>
                  <div className="mt-1.5 text-[0.74rem] text-muted">
                    L{active.residual.likelihood} × S{active.residual.severity}
                  </div>
                  <div className="text-[0.78rem] font-semibold text-ink">{RISK_BAND_LABEL[residualResult.band]}</div>
                </>
              ) : (
                <div className="mt-1.5 text-[0.78rem] leading-snug text-muted">
                  Add controls and re-score to show the residual rating
                </div>
              )}
            </div>
          </div>

          {/* Full register — one row per hazard. */}
          <TableScroll>
            <table className="mt-4 w-full min-w-[460px] text-[0.85rem]">
              <thead>
                <tr className="border-b border-line text-left text-[0.72rem] uppercase tracking-wide text-muted">
                  <th className="w-6 py-1.5 pr-2 font-semibold">#</th>
                  <th className="py-1.5 pr-2 font-semibold">Hazard & controls</th>
                  <th className="py-1.5 pr-2 text-right font-semibold">Initial</th>
                  <th className="py-1.5 text-right font-semibold">Residual</th>
                </tr>
              </thead>
              <tbody>
                {hazards.map((h, i) => {
                  const ini = rate(h.initial);
                  const res = residualCounts(h) ? rate(h.residual) : null;
                  return (
                    <tr key={h.id} className="border-b border-line/60 align-top">
                      <td className="py-2 pr-2 text-muted">{i + 1}</td>
                      <td className="py-2 pr-2">
                        <span className="font-semibold text-ink">{h.name.trim() || `Hazard ${i + 1}`}</span>
                        {h.controls.trim() && (
                          <span className="block text-[0.8rem] leading-snug text-muted">{h.controls.trim()}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-2 text-right font-semibold text-ink">
                        {h.initial.likelihood}×{h.initial.severity}={ini.score}{" "}
                        <span className="font-normal text-muted">({RISK_BAND_LABEL[ini.band]})</span>
                      </td>
                      <td className="whitespace-nowrap py-2 text-right font-semibold text-ink">
                        {res ? (
                          <>
                            {h.residual.likelihood}×{h.residual.severity}={res.score}{" "}
                            <span className="font-normal text-muted">({RISK_BAND_LABEL[res.band]})</span>
                          </>
                        ) : (
                          <span className="font-normal text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {assessor && (
                  <tr className="border-b border-line/60">
                    <td className="py-2 pr-2 text-muted" colSpan={2}>Assessed by</td>
                    <td className="py-2 text-right font-semibold text-ink" colSpan={2}>{assessor}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScroll>

          <p className="mt-3 text-[0.78rem] leading-relaxed text-muted">
            The score is a prioritisation aid only. It does not, on its own, make the work safe or compliant —
            a competent person must review the rating and controls for the specific task and site.
          </p>

          <PrintFooter toolName="risk matrix calculator" />
        </div>

        <Field label="">
          <TextInput
            value={assessor}
            onChange={(e) => setAssessor(e.target.value)}
            placeholder="Assessed by (for the printed record)"
          />
        </Field>

        <div className="flex flex-wrap justify-end gap-3">
          <CopyButton
            tool="risk-matrix-calculator"
            buildText={() => {
              const lines: string[] = [];
              lines.push(hazards.length > 1 ? "RISK RATING REGISTER" : "RISK RATING RECORD");
              hazards.forEach((h, i) => {
                const ini = rate(h.initial);
                const res = residualCounts(h) ? rate(h.residual) : null;
                lines.push("");
                lines.push(`${i + 1}. ${h.name.trim() || `Hazard ${i + 1}`}`);
                lines.push(
                  `   Initial risk: L${h.initial.likelihood} × S${h.initial.severity} = ${ini.score} (${RISK_BAND_LABEL[ini.band]})`,
                );
                if (res) {
                  if (h.controls.trim()) lines.push(`   Controls: ${h.controls.trim()}`);
                  lines.push(
                    `   Residual risk: L${h.residual.likelihood} × S${h.residual.severity} = ${res.score} (${RISK_BAND_LABEL[res.band]})`,
                  );
                }
              });
              if (assessor) {
                lines.push("");
                lines.push(`Assessed by: ${assessor}`);
              }
              return lines.join("\n");
            }}
          />
          <PrintButton tool="risk-matrix-calculator" label="Print / save record as PDF" />
        </div>
      </div>
    </div>
  );
}
